import { useEffect, useRef, useState } from 'react';
import { useClerk, useUser, UserButton } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import UpgradeModal from '@/components/UpgradeModal';
import * as analytics from '@/lib/analytics';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

const LANDING_IMAGES = {
  s1: '/assets/landing/lowest-future-balance.png',
  s2: '/assets/landing/2.png',
  s3a: '/assets/landing/3a.png',
  s3b: '/assets/landing/3b.png',
  s4a: '/assets/landing/4a.png',
  s4b: '/assets/landing/4b.png',
  s4c: '/assets/landing/4c.png',
  s5a: '/assets/landing/5a.png',
  s5b: '/assets/landing/5b.png',
};

function navigateToApp() {
  window.location.href = '/app';
}

function navigateToLearnMore() {
  window.location.href = '/learn';
}

// Real Clerk-aware button. Must only be mounted inside a ClerkProvider.
// Behaviour:
//   - If a Clerk session already exists, render the Clerk UserButton (avatar
//     + dropdown with Sign Out) so the user can clearly see they're signed
//     in and switch accounts if they want.
//   - Otherwise render a "Sign In" button that opens the Clerk modal.
function ClerkSignInButton() {
  const { openSignIn } = useClerk();
  const { isLoaded, isSignedIn } = useUser();

  if (isLoaded && isSignedIn) {
    return (
      <UserButton
        afterSignOutUrl="/"
        appearance={{ elements: { avatarBox: 'w-9 h-9' } }}
      />
    );
  }

  return (
    <Button
      size="sm"
      onClick={() => openSignIn({ afterSignInUrl: '/app', afterSignUpUrl: '/app' })}
      data-testid="landing-sign-in-btn"
      className="bg-black text-white hover:bg-black/85"
    >
      Sign In
    </Button>
  );
}

// Dev-mode fallback used when the placeholder Clerk key is in place.
// We can't call useClerk() outside ClerkProvider, so this just routes to /app
// where the dev-mode dashboard handles the rest.
function DevSignInButton() {
  return (
    <Button
      size="sm"
      onClick={navigateToApp}
      data-testid="landing-sign-in-btn"
      className="bg-black text-white hover:bg-black/85"
    >
      Sign In
    </Button>
  );
}

/* Intersection-observer hook — fades children in once they scroll into view */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('landing-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FadeSection({ children, className = '', ...props }) {
  const ref = useFadeIn();
  return (
    <section
      ref={ref}
      className={`landing-fade ${className}`}
      {...props}
    >
      {children}
    </section>
  );
}

export default function LandingPage({ devMode = false }) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useDocumentMeta({
    title: 'Cash Forth — Cash Flow Forecasting + Unlimited What Ifs',
    description: 'Cash Forth is a simple cash flow forecasting tool. See your future account balances with recurring income, expenses, and unlimited what-if scenarios.',
  });

  // If the URL carries ?source=pricing, auto-open the upgrade modal in
  // pricing mode (e.g. inbound link from Pricing button on another page).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('source') === 'pricing') {
      setUpgradeOpen(true);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Inject the fade-in keyframes once
    if (!document.getElementById('landing-fade-style')) {
      const style = document.createElement('style');
      style.id = 'landing-fade-style';
      style.textContent = `
        .landing-fade {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .landing-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="landing-page">
      {/* ── Fixed Banner ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80"
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img
                src="/assets/logo.png"
                alt="Cash Forth Logo"
                className="h-10 w-10 object-contain"
              />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight">
                  Cash Forth
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">
                  Cash Flow Forecast + Unlimited What Ifs
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  analytics.trackPricingClicked('header');
                  setUpgradeOpen(true);
                }}
                data-testid="landing-pricing-btn"
                className="hidden sm:inline-flex border-black/20 text-black hover:bg-black/5"
              >
                Pricing
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  analytics.trackTryItFreeClicked('header');
                  navigateToApp();
                }}
                data-testid="landing-try-free-btn"
                className="border-black/20 text-black hover:bg-black/5"
              >
                Try it for Free
              </Button>
              {devMode ? <DevSignInButton /> : <ClerkSignInButton />}
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16" />

      {/* ── Section 1 ── Centered stack: copy → image → Learn More */}
      <FadeSection
        className="min-h-[calc(100vh-4rem)] flex items-center py-6 md:py-12"
        data-testid="landing-section-1"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col items-center text-center gap-6 md:gap-8">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                How much should you keep in your checking account?
              </h2>
              <p className="mt-4 font-heading text-xl sm:text-2xl lg:text-3xl text-gray-600 leading-snug">
                Cash Forth shows your lowest future balance so you don&rsquo;t have to guess.
              </p>
            </div>
            <img
              src={LANDING_IMAGES.s1}
              alt="Chart showing today's balance and the lowest projected future balance"
              className="w-full max-w-3xl rounded-lg"
            />
            <div>
              <Button
                size="lg"
                onClick={() => {
                  analytics.trackLearnMoreClicked('section_1');
                  navigateToLearnMore();
                }}
                data-testid="landing-learn-more-section1-btn"
                className="bg-black text-white hover:bg-black/85 text-base px-8 py-5 rounded-md"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </FadeSection>

      {/* ── Section 2 ── Image left, bulleted list right */}
      <FadeSection
        className="min-h-[80vh] flex items-center py-12 md:py-0"
        data-testid="landing-section-2"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="order-2 md:order-1 flex justify-center">
              <img
                src={LANDING_IMAGES.s2}
                alt="Chart view with transaction tooltip"
                className="w-full max-w-lg rounded-lg shadow-lg"
              />
            </div>
            <ul className="order-1 md:order-2 space-y-6 list-none pl-0">
              <li className="font-heading text-xl sm:text-2xl lg:text-3xl leading-snug flex gap-3 sm:gap-4">
                <span aria-hidden="true" className="select-none">&bull;</span>
                <span>See up to 2 years of future transactions, in chronological order.</span>
              </li>
              <li className="font-heading text-xl sm:text-2xl lg:text-3xl leading-snug flex gap-3 sm:gap-4">
                <span aria-hidden="true" className="select-none">&bull;</span>
                <span>Spot the low points ahead of time.</span>
              </li>
              <li className="font-heading text-xl sm:text-2xl lg:text-3xl leading-snug flex gap-3 sm:gap-4">
                <span aria-hidden="true" className="select-none">&bull;</span>
                <span>Transfer excess cash to investments, high yield savings, or debt pay-down with precision and confidence.</span>
              </li>
            </ul>
          </div>
        </div>
      </FadeSection>

      {/* ── Section 3 ── Two images with captions */}
      <FadeSection
        className="min-h-[80vh] flex items-center py-12 md:py-0"
        data-testid="landing-section-3"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            <div>
              <p className="font-heading text-lg sm:text-xl text-gray-700 mb-4 text-center">
                Ledger view is like your bank statement&mdash;in the future.
              </p>
              <img
                src={LANDING_IMAGES.s3a}
                alt="Ledger view showing future transactions"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            <div>
              <p className="font-heading text-lg sm:text-xl text-gray-700 mb-4 text-center">
                Chart view helps you visualize lows, highs, and trends.
              </p>
              <img
                src={LANDING_IMAGES.s3b}
                alt="Chart view showing balance trends"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </FadeSection>

      {/* ── Section 4 ── Centered text top, 3 images below */}
      <FadeSection
        className="min-h-[80vh] flex items-center py-12 md:py-0 mb-16 md:mb-24"
        data-testid="landing-section-4"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight tracking-tight text-center max-w-4xl mx-auto mb-12">
            Cash Forth gives you the flexibility to combine all types of
            transactions and see how they affect your future running balance.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
              <img
                src={LANDING_IMAGES.s4a}
                alt="Add Transaction form"
                className="w-full rounded-lg shadow-lg"
              />
              <img
                src={LANDING_IMAGES.s4b}
                alt="Hypothetical transaction toggle"
                className="w-full rounded-lg shadow-lg"
              />
              <img
                src={LANDING_IMAGES.s4c}
                alt="Customize Future Amounts"
                className="w-full rounded-lg shadow-lg"
              />
          </div>
        </div>
      </FadeSection>

      {/* ── Section 5 ── Two images with captions (larger, centered) */}
      <FadeSection
        className="min-h-[60vh] flex items-center pt-16 md:pt-24 py-12 md:py-0"
        data-testid="landing-section-5"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            <div>
              <p className="font-heading text-xl sm:text-2xl lg:text-3xl text-gray-700 mb-4 text-center max-w-[85%] mx-auto">
                Set threshold alerts to catch problems early
              </p>
              <img
                src={LANDING_IMAGES.s5a}
                alt="Threshold alerts on the forecast chart"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            <div>
              <p className="font-heading text-xl sm:text-2xl lg:text-3xl text-gray-700 mb-4 text-center max-w-[85%] mx-auto">
                Simulate hypotheticals for smarter planning
              </p>
              <img
                src={LANDING_IMAGES.s5b}
                alt="Hypothetical transaction in the forecast"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </FadeSection>

      {/* ── Section 6 ── Centered CTA */}
      <FadeSection
        className="min-h-[70vh] flex items-center justify-center"
        data-testid="landing-section-6"
      >
        <div className="text-center px-4">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight max-w-3xl mx-auto">
            See the future. Make better decisions today. Enjoy the peace of mind!
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => {
                analytics.trackTryItFreeClicked('section_6_start_forecasting');
                navigateToApp();
              }}
              data-testid="landing-start-btn"
              className="bg-black text-white hover:bg-black/85 text-base px-8 py-5 rounded-md"
            >
              Start Forecasting
            </Button>
            <Button
              size="lg"
              onClick={() => {
                analytics.trackPricingClicked('section_6');
                setUpgradeOpen(true);
              }}
              data-testid="landing-pricing-section6-btn"
              className="bg-black text-white hover:bg-black/85 text-base px-8 py-5 rounded-md"
            >
              Pricing
            </Button>
            <Button
              size="lg"
              onClick={() => {
                analytics.trackLearnMoreClicked('section_6');
                navigateToLearnMore();
              }}
              data-testid="landing-learn-more-section6-btn"
              className="bg-black text-white hover:bg-black/85 text-base px-8 py-5 rounded-md"
            >
              Learn More
            </Button>
          </div>
        </div>
      </FadeSection>

      {/* Minimal footer */}
      <footer className="py-6 text-center text-xs text-gray-400 border-t border-gray-100">
        &copy; 2026 Cash Forth. All rights reserved.
      </footer>

      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        mode="pricing"
        devMode={devMode}
      />
    </div>
  );
}
